import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";

const ComplaintList = () => {
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();

    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const employeeCode = storageService.getItem("employeeCode");
    const roleId = Number(storageService.getItem("roleId"));

    const fetchComplaints = async () => {
        setLoading(true);
        try {
            let currentSiteId = null;
            let currentUserId = employeeCode;

            if (employeeCode) {
                const userRes = await apiServices({
                    url: '/ResidentialUser/GetResidentialUserById',
                    method: 'POST',
                    data: { userId: employeeCode }
                });
                if (userRes.success && userRes.data?.data?.[0]) {
                    currentSiteId = userRes.data.data[0].siteId;
                    currentUserId = userRes.data.data[0].userId;
                }
            }

            if (!currentSiteId) {
                setComplaints([]);
                setLoading(false);
                return;
            }

            // In Angular, if Role = 3 (Admin) it gets by SiteId, else by UserId.
            const url = roleId === 3 || roleId === 1
                ? `/ResidentialComplaint/GetAllComplaintsBySiteId/${currentSiteId}`
                : `/ResidentialComplaint/GetComplaintsByUserId/${currentUserId}`;

            const res = await apiServices({
                url,
                method: 'GET'
            });

            if (res.success && res.data?.data) {
                const mapped = res.data.data.map(c => ({
                    complaintId: c.complaintId || c.ComplaintId,
                    complaintTitle: c.complaint_title || c.ComplaintTitle || 'Unknown',
                    complaintDescription: c.description || c.ComplaintDescription || '-',
                    status: c.status || c.Status,
                    date: c.createdOn || c.Date,
                    userId: c.userId || c.UserId,
                    userName: c.userName || c.UserName || c.FlatNumber || `User`,
                    image: c.imageUrl && c.imageUrl !== 'NO_IMAGE' ? c.imageUrl : (c.Image || null),
                    category: c.category || '-',
                    priority: c.priority || c.remark || '-'
                }));
                mapped.sort((a, b) => b.complaintId - a.complaintId);
                setComplaints(mapped);
                setPagination(prev => ({ ...prev, totalRecords: mapped.length }));
            } else {
                setComplaints([]);
                setPagination(prev => ({ ...prev, totalRecords: 0 }));
            }
        } catch (error) {
            console.error("Error fetching complaints:", error);
            toast.error("Failed to fetch complaints");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComplaints();
    }, []);

    const handleEdit = (row) => {
        navigate(`/zentres/ComplaintList/edit/${row.complaintId}`);
    };

    const handleDelete = async (row) => {
        if (!window.confirm("Are you sure you want to delete this complaint?")) return;
        try {
            // Check if there is a delete endpoint, otherwise this is a placeholder
            // Many modules just use status=Inactive or a delete endpoint if it exists
            const res = await apiServices({
                url: `/ResidentialComplaint/DeleteComplaint/${row.complaintId}`,
                method: 'DELETE'
            });
            if (res.success) {
                toast.success("Complaint deleted successfully");
                fetchComplaints();
            } else {
                toast.error(res.data?.Message || "Failed to delete complaint");
            }
        } catch (error) {
            console.error("Error deleting complaint:", error);
            toast.error("Failed to delete complaint");
        }
    };

    const handleAddNew = () => {
        navigate('/zentres/ComplaintList/add');
    };

    const onPageChange = (page) => {
        setPagination(prev => ({ ...prev, pageNumber: page }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
    };

    const filteredComplaints = complaints.filter(c =>
        c.complaintTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.complaintDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const tableHeaders = [
        { label: "S. No", key: "sno", align: "center" },
        { label: "TITLE", key: "complaintTitle", align: "left" },
        { label: "RAISED BY", key: "raisedBy", align: "left" },
        { label: "DATE", key: "date", align: "center" },
        { label: "STATUS", key: "status", align: "center" },
        { label: "ACTIONS", key: "actions", align: "center" }
    ];

    const getStatusStyles = (status) => {
        if (!status) return 'bg-gray-100 text-gray-600';
        const normalized = status.toLowerCase();
        if (normalized === 'resolved' || normalized === 'closed') return 'bg-[#DCFCE7] text-[#166534]';
        if (normalized === 'open' || normalized === 'pending') return 'bg-[#FEE2E2] text-[#991B1B]';
        if (normalized === 'in-progress' || normalized === 'in progress') return 'bg-[#DBEAFE] text-[#1E40AF]';
        return 'bg-gray-100 text-gray-600';
    };

    const renderTableCell = (key, row, idx) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{((pagination.pageNumber - 1) * pagination.pageSize) + row.originalIndex + 1}</span>;
            case "complaintTitle":
                return (
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-xs text-[#191C1E]">{row.complaintTitle}</span>
                        <span className="text-[10px] text-gray-500 truncate max-w-[200px]" title={row.complaintDescription}>
                            {row.complaintDescription}
                        </span>
                    </div>
                );
            case "raisedBy":
                return <span className="text-xs text-gray-700">{row.userName || row.userId}</span>;
            case "date":
                return <span className="text-xs text-gray-700">{row.date ? new Date(row.date).toLocaleDateString() : '—'}</span>;
            case "status":
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${getStatusStyles(row.status)}`}>
                        {row.status || "—"}
                    </span>
                );
            case "actions":
                return null;
            default:
                return row[key] || "—";
        }
    };

    const expandedRowRender = (row) => {
        return (
            <div className="p-6 bg-white border border-gray-100 rounded-xl shadow-sm m-4 flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-1/3 flex-shrink-0">
                    <div className="w-full h-48 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                        {row.image ? (
                            <img src={row.image} alt={row.complaintTitle} className="w-full h-full object-cover" />
                        ) : (
                            <div className="text-gray-400 text-xs font-bold uppercase tracking-wider">No Image Provided</div>
                        )}
                    </div>
                </div>

                <div className="flex-1 grid grid-cols-2 gap-y-6 gap-x-8">
                    <div className="col-span-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Full Description</span>
                        <p className="text-sm text-gray-800 leading-relaxed font-medium">{row.complaintDescription}</p>
                    </div>
                    <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Category</span>
                        <p className="text-sm font-bold text-[#1E293B]">{row.category}</p>
                    </div>
                    <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Priority / Remark</span>
                        <p className="text-sm font-bold text-[#1E293B]">{row.priority}</p>
                    </div>
                </div>
            </div>
        );
    };

    const headerAction = (
        <div className="flex items-center gap-3">
            <div className="relative w-64 hidden sm:block">
                <input
                    type="text"
                    placeholder="Search complaints..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-xl text-xs font-semibold text-gray-700 outline-none focus:border-red-500 transition-colors bg-gray-50/50"
                />
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            </div>
        </div>
    );

    const displayData = filteredComplaints.slice((pagination.pageNumber - 1) * pagination.pageSize, pagination.pageNumber * pagination.pageSize);

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                mainHeading="Complaints List"
                subHeading="Manage your residential complaints."
                primaryBtnText="Raise Complaint"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={handleAddNew}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    loading={loading}
                    tableHeading="Complaints List"
                    headers={tableHeaders}
                    data={displayData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row, idx) => renderTableCell(key, row, idx)}
                    headerAction={headerAction}
                    pagination={{ ...pagination, totalRecords: filteredComplaints.length }}
                    onPageChange={onPageChange}
                    onLimitChange={handleLimitChange}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    expandedRowRender={expandedRowRender}
                />
            </div>
        </div>
    );
};

export default ComplaintList;
